/**
 * Compact AI Workflow Node Component
 *
 * Compact design with:
 * - Small box with 1-2 word title
 * - Top-left corner: node type color indicator
 * - Top-right corner: execution status color indicator
 * - Hover tooltip with full details
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position } from 'reactflow';
import ReactMarkdown from 'react-markdown';
import {
  Clock,
  CheckCircle,
  XCircle,
  PlayCircle,
  AlertCircle,
  DollarSign,
  Zap,
  Loader,
  CheckCircle2,
  Pause,
  Database,
  Sparkles,
  GitMerge,
  User,
  ListTree,
  Bell
} from 'lucide-react';

// Add CSS for blinking animation
const blinkingStyles = `
  @keyframes blink-alert {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.3;
      transform: scale(1.2);
    }
  }

  .blink-alert {
    animation: blink-alert 1s ease-in-out infinite;
  }
`;

// Max characters to show in tooltip output before truncating
const TOOLTIP_OUTPUT_MAX_CHARS = 500;

/**
 * Get status color for corner indicator
 */
const getStatusColor = (status) => {
  const colors = {
    pending: '#9CA3AF',     // gray-400
    ready: '#3B82F6',       // blue-500
    running: '#3B82F6',     // blue-500
    completed: '#10B981',   // green-500
    failed: '#EF4444',      // red-500
    skipped: '#6B7280',     // gray-500
    waiting_approval: '#F97316'  // orange-500
  };
  return colors[status] || colors.pending;
};

/**
 * Get node type color
 */
const getNodeTypeColor = (nodeType) => {
  const colors = {
    data_retrieval: '#3B82F6',    // blue
    llm_analysis: '#8B5CF6',      // purple
    reconciliation: '#F97316',    // orange
    human_approval: '#EAB308',    // yellow
    decomposition: '#14B8A6'      // teal
  };
  return colors[nodeType] || '#6B7280';
};

/**
 * Get node type icon
 */
const getNodeTypeIcon = (nodeType) => {
  const icons = {
    data_retrieval: Database,
    llm_analysis: Sparkles,
    reconciliation: GitMerge,
    human_approval: User,
    decomposition: ListTree
  };
  return icons[nodeType] || Database;
};

/**
 * Get short node type label
 */
const getNodeTypeLabel = (nodeType) => {
  const labels = {
    data_retrieval: 'Data',
    llm_analysis: 'LLM',
    reconciliation: 'Merge',
    human_approval: 'Approval',
    decomposition: 'Breakdown'
  };
  return labels[nodeType] || nodeType;
};

/**
 * Get status label
 */
const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pending',
    ready: 'Ready',
    running: 'Running',
    completed: 'Done',
    failed: 'Failed',
    skipped: 'Skipped',
    waiting_approval: 'Waiting'
  };
  return labels[status] || status;
};

/**
 * Truncate text to a max length with ellipsis
 */
const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return { text, truncated: false };
  return { text: text.slice(0, maxLength) + '…', truncated: true };
};

/**
 * Hover tooltip with full node details
 * Rendered as a portal to escape React Flow's stacking context
 */
const NodeTooltip = ({ data, show, position, onMouseEnter, onMouseLeave }) => {
  if (!show || !position) return null;

  const {
    displayName,
    description,
    nodeType,
    status,
    complexity,
    requiresApproval,
    approvalPrompt,
    cost,
    tokensInput,
    tokensOutput,
    humanOutput,
    error,
    modelName,
    capabilityLabels = []
  } = data;

  const typeColor = getNodeTypeColor(nodeType);
  const statusColor = getStatusColor(status);

  // Truncate output for tooltip display
  const outputDisplay = truncateText(humanOutput, TOOLTIP_OUTPUT_MAX_CHARS);

  const tooltipContent = (
    <div
      className="fixed w-96 bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-4"
      style={{
        left: `${position.x + 140 + 16}px`,
        top: `${position.y}px`,
        zIndex: 10000
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="font-semibold text-lg text-gray-900">{displayName}</h3>
        <div className="flex items-center gap-2 mt-2">
          <span
            className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
            style={{ backgroundColor: typeColor }}
          >
            {getNodeTypeLabel(nodeType)}
          </span>
          <span
            className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
            style={{ backgroundColor: statusColor }}
          >
            {getStatusLabel(status)}
          </span>
          {complexity && (
            <span className="text-xs text-gray-500">
              Complexity: {complexity}/10
            </span>
          )}
        </div>

        {/* Model Info */}
        {modelName && (
          <div className="text-xs text-gray-600 mt-2">
            <span className="font-medium">Model:</span> {modelName}
          </div>
        )}

        {/* Capability Badges */}
        {capabilityLabels && capabilityLabels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {capabilityLabels.map((capability, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200"
              >
                {capability}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Description — rendered as markdown */}
      {description && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-600 mb-1">Description:</div>
          <div className="text-sm text-gray-800 prose prose-sm max-w-none">
            <ReactMarkdown>{description}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Approval Prompt — rendered as markdown */}
      {approvalPrompt && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-600 mb-1">Approval Prompt:</div>
          <div className="text-sm text-gray-800 bg-yellow-50 p-2 rounded prose prose-sm max-w-none">
            <ReactMarkdown>{approvalPrompt}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-3">
          <div className="text-xs font-medium text-red-600 mb-1">Error:</div>
          <div className="text-sm text-red-800 bg-red-50 p-2 rounded">{error}</div>
        </div>
      )}

      {/* Output — rendered as markdown, truncated */}
      {humanOutput && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-600 mb-1">Output:</div>
          <div className="text-sm text-gray-800 bg-gray-50 p-2 rounded prose prose-sm max-w-none">
            <ReactMarkdown>{outputDisplay.text}</ReactMarkdown>
          </div>
          {outputDisplay.truncated && (
            <div className="text-xs text-blue-600 mt-1 italic">
              Click node for full output
            </div>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="pt-3 border-t border-gray-200 space-y-2">
        {cost !== undefined && cost !== null && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Cost:</span>
            <span className="font-medium text-gray-900">${cost.toFixed(6)}</span>
          </div>
        )}
        {(tokensInput || tokensOutput) && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Tokens:</span>
            <span className="font-medium text-gray-900">
              {tokensInput || 0} in / {tokensOutput || 0} out
            </span>
          </div>
        )}
        {data.attachedFiles && data.attachedFiles.length > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Attached Files:</span>
            <span className="font-medium text-blue-700">
              {data.attachedFiles.length} file{data.attachedFiles.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
        {requiresApproval && (
          <div className="flex items-center gap-2 text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded">
            <AlertCircle size={14} />
            <span>Requires Approval</span>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

/**
 * Compact AI Workflow Node
 */
export default function AIWorkflowBaseNode({ id, data, children }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const nodeRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const {
    nodeKey,
    displayName,
    nodeType,
    status = 'pending',
    onSelect
  } = data;

  // Bridge pattern: delayed hide so mouse can travel from node to tooltip
  const scheduleHide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 150);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleNodeMouseEnter = useCallback(() => {
    cancelHide();
    setShowTooltip(true);
  }, [cancelHide]);

  const handleNodeMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  const handleTooltipMouseEnter = useCallback(() => {
    cancelHide();
  }, [cancelHide]);

  const handleTooltipMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  // Inject CSS for blinking animation
  useEffect(() => {
    const styleId = 'node-blinking-styles';
    if (!document.getElementById(styleId)) {
      const styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.textContent = blinkingStyles;
      document.head.appendChild(styleTag);
    }
  }, []);

  // Cleanup timeout and tooltip on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      setShowTooltip(false);
    };
  }, []);

  const handleClick = () => {
    if (onSelect) {
      onSelect(nodeKey);
    }
  };

  // Update tooltip position when hovering
  useEffect(() => {
    if (showTooltip && nodeRef.current) {
      const rect = nodeRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left,
        y: rect.top
      });
    } else {
      setTooltipPosition(null);
    }
  }, [showTooltip]);

  const typeColor = getNodeTypeColor(nodeType);
  const statusColor = getStatusColor(status);
  const TypeIcon = getNodeTypeIcon(nodeType);

  // Shorten display name to 1-2 words
  const shortName = displayName?.split(' ').slice(0, 2).join(' ') || 'Node';

  // Determine border thickness and glow effect based on status
  const getBorderStyle = () => {
    const baseStyle = {
      borderStyle: 'solid',
      borderColor: statusColor,
      width: '140px',
      height: '70px'
    };

    if (status === 'running') {
      return {
        ...baseStyle,
        borderWidth: '3px',
        boxShadow: `0 0 20px ${statusColor}80, 0 4px 6px -1px rgba(0, 0, 0, 0.1)`
      };
    } else if (status === 'failed') {
      return {
        ...baseStyle,
        borderWidth: '3px',
        boxShadow: `0 0 16px ${statusColor}60, 0 4px 6px -1px rgba(0, 0, 0, 0.1)`
      };
    } else if (status === 'waiting_approval') {
      return {
        ...baseStyle,
        borderWidth: '3px',
        boxShadow: `0 0 16px ${statusColor}60, 0 4px 6px -1px rgba(0, 0, 0, 0.1)`
      };
    } else if (status === 'completed') {
      return {
        ...baseStyle,
        borderWidth: '2px',
        boxShadow: `0 0 10px ${statusColor}40`
      };
    }

    // pending / ready / skipped — subtle border only, no glow
    return {
      ...baseStyle,
      borderWidth: '1px',
      borderColor: '#E5E7EB'  // gray-200 — nearly invisible at rest
    };
  };

  return (
    <div
      ref={nodeRef}
      className="relative"
      onMouseEnter={handleNodeMouseEnter}
      onMouseLeave={handleNodeMouseLeave}
    >
      {/* Compact node box */}
      <div
        className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer relative ${
          status === 'running' ? 'animate-pulse-strong' : ''
        }`}
        style={getBorderStyle()}
        onClick={handleClick}
      >
        {/* Top-left corner: Node type indicator */}
        <div
          className="absolute top-0 left-0 w-6 h-6 rounded-tl-lg rounded-br-lg flex items-center justify-center"
          style={{ backgroundColor: typeColor }}
          title={getNodeTypeLabel(nodeType)}
        >
          <TypeIcon size={14} className="text-white" />
        </div>

        {/* Top-right corner: Status indicator */}
        <div
          className="absolute top-0 right-0 w-6 h-6 rounded-tr-lg rounded-bl-lg"
          style={{ backgroundColor: statusColor }}
          title={getStatusLabel(status)}
        >
          {status === 'running' && (
            <Loader size={14} className="text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-spin" />
          )}
          {status === 'completed' && (
            <CheckCircle2 size={14} className="text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          )}
          {status === 'failed' && (
            <XCircle size={14} className="text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          )}
          {status === 'waiting_approval' && (
            <Bell size={16} className="text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 blink-alert" />
          )}
        </div>

        {/* Additional prominent alert icon for waiting_approval - outside the node box */}
        {status === 'waiting_approval' && (
          <div
            className="absolute -top-3 -right-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center shadow-lg blink-alert"
            style={{ zIndex: 10 }}
            title="Approval Required!"
          >
            <Bell size={18} className="text-white" />
          </div>
        )}

        {/* Node title (center) */}
        <div className="absolute inset-0 flex items-center justify-center px-8">
          <div className="text-sm font-medium text-gray-900 text-center leading-tight truncate">
            {shortName}
          </div>
        </div>

        {/* React Flow handles */}
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: typeColor,
            width: 8,
            height: 8,
            top: -4
          }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: typeColor,
            width: 8,
            height: 8,
            bottom: -4
          }}
        />
      </div>

      {/* Hover tooltip - rendered as portal */}
      <NodeTooltip
        data={data}
        show={showTooltip}
        position={tooltipPosition}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      />
    </div>
  );
}
